import { TokenRefreshLink } from "apollo-link-token-refresh";

import { useAuth, TypesAuth, DefaultJWTProps, UserJWTProps } from "../../Store";
import { isTokenExpired, setTokens } from "../services/Graphql.actions";

interface LegalPersonJWTProps {
  accessToken?: string;
  expiresIn?: string;
  accounts?: {
    store?: string;
    fantasyName?: string;
  };
}

interface UpdatedJWTsProps {
  hubAccessToken?: string;
  userAccessToken?: string;
  legalPersonAccessToken?: string;
}

export const useRefreshTokens = (): {
  refreshTokens(updateJWT: UpdatedJWTsProps): any;
} => {
  const [stateAuth, dispatchAuth] = useAuth();

  const userAccessToken = stateAuth?.userJWT?.accessToken;
  const hubAccessToken = stateAuth?.hubJWT?.accessToken;
  const legalPersonAccessToken = stateAuth?.legalPersonJWT?.accessToken;
  const userJWTRefreshToken = stateAuth.userJWT.refreshToken;

  const refreshTokens = (updateJWT: UpdatedJWTsProps): any =>
    new TokenRefreshLink({
      isTokenValidOrUndefined: async (): Promise<boolean> =>
        !isTokenExpired({
          hubAccessToken,
          userAccessToken,
          legalPersonAccessToken,
        }),
      fetchAccessToken: async (): Promise<Response> => {
        return await setTokens({
          hubAccessToken,
          userAccessToken,
          legalPersonAccessToken,
          userJWTRefreshToken,
        });
      },
      handleResponse:
        () =>
        (
          response: any
        ): {
          access_token: {
            hubJWT?: DefaultJWTProps;
            userJWT?: UserJWTProps;
            legalPersonJWT: LegalPersonJWTProps;
          };
        } => {
          if (
            !response?.userJWT &&
            !response?.hubJWT &&
            !response?.legalPersonJWT
          ) {
            return response;
          }

          let userJWT: UserJWTProps = {
            accessToken: "",
            idToken: "",
            expiresIn: "",
            refreshToken: "",
          };

          let hubJWT: DefaultJWTProps = {
            accessToken: "",
            expiresIn: "",
          };

          let legalPersonJWT: LegalPersonJWTProps = {
            accessToken: "",
            expiresIn: "",
            accounts: {},
          };

          if (response?.hubJWT) {
            hubJWT = {
              accessToken: response?.hubJWT?.access_token,
              expiresIn: response?.hubJWT?.expires_in,
            };
          }

          if (response?.userJWT) {
            userJWT = {
              accessToken: response?.userJWT?.access_token,
              idToken: response?.userJWT?.id_token,
              expiresIn: response?.userJWT?.expires_in,
              refreshToken: stateAuth.userJWT.refreshToken,
            };
          }

          if (response?.legalPersonJWT) {
            legalPersonJWT = {
              accessToken: response?.legalPersonJWT?.access_token,
              expiresIn: response?.legalPersonJWT?.expires_in,
              accounts: {
                fantasyName: response?.legalPersonJWT?.accounts?.fantasy_name,
                store: response?.legalPersonJWT?.accounts?.store,
              },
            };
          }

          return {
            access_token: {
              userJWT,
              hubJWT,
              legalPersonJWT,
            },
          };
        },
      handleFetch: (tokens: {
        hubJWT?: DefaultJWTProps;
        userJWT?: UserJWTProps;
        legalPersonJWT?: LegalPersonJWTProps;
      }): void => {
        const payload: {
          hubJWT?: DefaultJWTProps;
          userJWT?: UserJWTProps;
          legalPersonJWT?: {
            accessToken?: string;
            expiresIn?: string;
            document?: string;
            username?: string;
          };
        } = {};

        if (tokens?.hubJWT?.accessToken) {
          updateJWT.hubAccessToken = tokens.hubJWT.accessToken;
          payload.hubJWT = {
            accessToken: tokens.hubJWT.accessToken,
            expiresIn: tokens.hubJWT?.expiresIn,
          };
        }

        if (tokens?.userJWT?.accessToken) {
          updateJWT.userAccessToken = tokens.userJWT.accessToken;
          payload.userJWT = {
            accessToken: tokens.userJWT.accessToken,
            expiresIn: tokens.userJWT?.expiresIn,
            idToken: tokens.userJWT?.idToken,
            refreshToken: tokens.userJWT?.refreshToken,
          };
        }

        if (tokens?.legalPersonJWT?.accessToken) {
          updateJWT.legalPersonAccessToken = tokens.legalPersonJWT.accessToken;
          payload.legalPersonJWT = {
            accessToken: tokens.legalPersonJWT.accessToken,
            expiresIn: tokens.legalPersonJWT?.expiresIn,
            document: tokens.legalPersonJWT?.accounts?.store,
            username: tokens.legalPersonJWT?.accounts?.fantasyName,
          };
        }

        if (tokens) {
          dispatchAuth({
            type: TypesAuth.setTokens,
            payload,
          });
        }
      },
      handleError: (err): any => {
        console.log("Your refresh token is invalid. Try to relogin");
        console.error("refreshTokens - handleError", err);
        return err;
      },
    });

  return { refreshTokens };
};
